import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Layers,
  LogOut,
  Package,
  User,
  Brain,
  Zap,
  Network,
  Settings,
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { usePanel } from "@/context/panel-context";
import { useAuth } from "react-oidc-context";
import CustomTooltip from "../elements/custom-tooltip";
import { usePanelProps } from "@/context/panel-props-context";
import { TPanel } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { broadcastLogout } from "@/lib/auth-sync";
import { useUser } from "@/hooks/use-user";

const iconSize = { width: "1.25rem", height: "1.25rem" };

export default function Sidebar() {
  const { pathname } = useLocation();
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const { user } = useUser();

  const { setPanelType, panelType } = usePanel();
  const { setPanelProps } = usePanelProps();
  const isApiKeyPage = pathname === "/mcp/generate/key";
  const showMenuOptions = pathname !== "/" || isApiKeyPage;
  // BREEZEAI-1127: while in Settings, the project-scoped nav items are
  // disabled — Settings is a user-level destination and clicking a
  // project-scoped link from here would silently drop the user out of the
  // Settings context.
  const isSettings = pathname.startsWith("/settings");
  const DISABLED_IN_SETTINGS = new Set([
    "Dashboard",
    "Artifacts",
    "Ontology Generation",
    "Semantic Model",
  ]);

  const [isHovered, setIsHovered] = useState(false);

  const handleLogout = () => {
    // Broadcast logout to all other tabs
    broadcastLogout();
    // Clear current tab's sessionStorage
    sessionStorage.clear();
    // Perform logout redirect
    auth.signoutRedirect();
  };

  const handleRightPanel = (panelName: TPanel) => {
    if (panelName === panelType) {
      setPanelType(null);
    } else {
      if (panelName === "UNIFIED_MODEL") {
        setPanelProps({ mode: "unified_model", uuid: id });
      }
      setPanelType(panelName);
    }
  };


  const navigateWithResetPanel = (path: string) => {
    navigate(path);
    setPanelType(null);
  };

  // Helper function to determine if menu item is active
  const isItemActive = (text: string) => {
    switch (text) {
      case "Projects":
        return pathname === "/";
      case "Dashboard":
        return pathname.startsWith("/dashboard");
      case "Artifacts":
        // Trailing slash matters — `/knowledge-graph/:id` also starts with
        // "/knowledge" and used to light up Artifacts by mistake when the
        // user opened the knowledge graph from the dashboard.
        return pathname.startsWith("/knowledge/");
      case "Ontology Generation":
        return pathname.startsWith("/ontology");
      case "Semantic Model":
        return panelType === "UNIFIED_MODEL";
      default:
        return false;
    }
  };

  const allMenuItems = [
    {
      text: "Projects",
      icon: Layers,
      onClick: () => navigateWithResetPanel("/"),
      isActive: isItemActive("Projects"),
    },
    {
      text: "Dashboard",
      icon: Brain,
      onClick: () => {
        if (!id) {
          toast.error("Please select a project first");
          return;
        }
        navigateWithResetPanel(`/dashboard/${id}`);
      },
      isActive: isItemActive("Dashboard"),
    },
    {
      text: "Artifacts",
      icon: Zap,
      onClick: () => {
        if (!id) {
          toast.error("Please select a project first");
          return;
        }
        navigateWithResetPanel(`/knowledge/${id}`);
      },
      isActive: isItemActive("Artifacts"),
    },
    {
      text: "Ontology Generation",
      icon: Network,
      onClick: () => {
        if (!id) {
          toast.error("Please select a project first");
          return;
        }
        navigateWithResetPanel(`/ontology/${id}/functional`);
      },
      isActive: isItemActive("Ontology Generation"),
    },
    {
      text: "Semantic Model",
      icon: Package,
      onClick: () => {
        handleRightPanel("UNIFIED_MODEL");
      },
      isActive: isItemActive("Semantic Model"),
    },
  ];

  // Only show Projects tab on API key generation page
  const menuItems = isApiKeyPage
    ? allMenuItems.filter((item) => item.text === "Projects")
    : allMenuItems;


  return (
    <>
      <aside
        className={cn(
          "fixed left-0 top-0 h-screen bg-background border-r border-light-only shadow-light-sidebar flex flex-col transition-all duration-300 ease-in-out overflow-hidden z-50",
          isHovered ? "w-60" : "w-20"
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Header with Logo */}
        <div className="h-16 flex items-center px-4 border-b border-light-only">
          <div className="flex items-center space-x-3 overflow-hidden">
            <div className="flex items-center justify-center flex-shrink-0">
              <img
                loading="lazy"
                src="/breezeAi.png"
                alt="Breeze.AI"
                width={24}
                height={24}
                className="rounded-md"
              />
            </div>
            {isHovered && (
              <span
                className={cn(
                  "font-semibold text-text-primary text-lg whitespace-nowrap transition-all duration-300 truncate",
                  isHovered
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 -translate-x-2"
                )}
              >
                Breeze.AI
              </span>
            )}
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 py-4 px-2" aria-label="Primary">
          {showMenuOptions && (
            <ul className="space-y-1" role="list">
              {menuItems.map(({ text, icon: Icon, onClick, isActive }) => {
                const iconColor = isActive ? "#FFFFFF" : "currentColor";
                const textColor = isActive ? "text-button-text" : "text-text-primary";
                const isDisabled = isSettings && DISABLED_IN_SETTINGS.has(text);
                const tooltipText = isDisabled ? `${text} — available outside Settings` : text;
                // Show a tooltip whenever the sidebar is collapsed (icon-only)
                // OR the item is disabled — in the disabled case the visible
                // label is already there but the user needs to know WHY it
                // won't respond to clicks (BREEZEAI-1127).
                const showTooltip = !isHovered || isDisabled;

                const buttonNode = !isHovered ? (
                  <Button
                    variant="ghost"
                    aria-disabled={isDisabled}
                    className={cn(
                      "w-full h-11 px-3 justify-center hover:bg-surface-hover transition-colors duration-200",
                      isActive ? "bg-primary hover:bg-primary-dark" : textColor,
                      isDisabled && "opacity-40 cursor-not-allowed hover:bg-transparent"
                    )}
                    onClick={isDisabled ? undefined : onClick}
                    aria-label={text}
                    aria-current={isActive ? "page" : undefined}
                  >
                    <div className="flex items-center justify-center w-6 h-6">
                      <Icon color={iconColor} style={iconSize} aria-hidden="true" />
                    </div>
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    aria-disabled={isDisabled}
                    className={cn(
                      "w-full h-11 px-3 justify-start hover:bg-surface-hover transition-colors duration-200",
                      isActive ? "bg-primary hover:bg-primary-dark" : textColor,
                      isDisabled && "opacity-40 cursor-not-allowed hover:bg-transparent"
                    )}
                    onClick={isDisabled ? undefined : onClick}
                    aria-label={text}
                    aria-current={isActive ? "page" : undefined}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center w-6 h-6 flex-shrink-0">
                        <Icon color={iconColor} style={iconSize} aria-hidden="true" />
                      </div>
                      <span
                        className={cn(
                          `${textColor} font-medium whitespace-nowrap transition-all duration-300`,
                          isHovered
                            ? "opacity-100 translate-x-0"
                            : "opacity-0 -translate-x-2"
                        )}
                        aria-hidden="true"
                      >
                        {text}
                      </span>
                    </div>
                  </Button>
                );

                return (
                  <li key={text} className="relative">
                    {showTooltip ? (
                      <CustomTooltip text={tooltipText}>{buttonNode}</CustomTooltip>
                    ) : (
                      buttonNode
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </nav>

        {/* Footer with User Profile */}
        <div className="p-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="w-full h-11 px-2 justify-start hover:bg-surface-hover transition-colors duration-200 focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                aria-label="User profile menu"
              >
                <div className="flex items-center space-x-3 w-full min-w-0">
                  <div className="w-12 h-12 bg-surface-active rounded-full flex items-center justify-center flex-shrink-0">
                    <User
                      className="text-primary"
                      style={{ width: "1.2rem", height: "1.2rem" }}
                      aria-hidden="true"
                    />
                  </div>
                  {isHovered && (
                    <div
                      className={cn(
                        "flex-1 min-w-0 text-left transition-all duration-300",
                        isHovered
                          ? "opacity-100 translate-x-0"
                          : "opacity-0 -translate-x-2"
                      )}
                    >
                      <div
                        className="text-text-primary font-medium text-sm truncate"
                        title={`${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim() || undefined}
                      >
                        {`${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim() || "Profile"}
                      </div>
                      {user?.email && (
                        <div
                          className="text-muted text-xs truncate"
                          title={user.email}
                        >
                          {user.email}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-48 bg-surface border border-surface-border rounded-md shadow-md"
            >
              {/* Settings is the single destination for what used to be four
                  separate menu items — themes, API key, user management, Jira
                  boards. The tabs inside gate themselves by role. */}
              <DropdownMenuItem
                onClick={() => navigate("/settings")}
                className="flex items-center hover:bg-surface-hover text-text-primary space-x-2 p-2 cursor-pointer focus:bg-surface-hover focus:text-text-primary"
              >
                <Settings className="h-4 w-4" />
                <span>Settings</span>
              </DropdownMenuItem>

              <DropdownMenuSeparator className="bg-surface-border" />

              <DropdownMenuItem
                onClick={handleLogout}
                className="flex items-center hover:bg-surface-hover text-text-primary space-x-2 p-2 cursor-pointer focus:bg-surface-hover focus:text-text-primary"
              >
                <LogOut className="h-4 w-4" />
                <span>Logout</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Trademark */}
        <div className="py-4 border-t border-light-only">
          <a
            href="https://www.accionlabs.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit Accion Labs website"
            className={cn(
              "flex items-center group transition-all",
              isHovered ? "px-4 justify-start space-x-3" : "px-0 justify-center"
            )}
          >
            <div className="w-12 h-8 flex items-center justify-center flex-shrink-0">
              <span aria-hidden="true" className="text-xl text-muted group-hover:text-primary transition-colors">™</span>
            </div>
            {isHovered && (
              <div
                className={cn(
                  "transition-all duration-300 overflow-hidden",
                  isHovered ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2"
                )}
              >
                <p className="text-xs text-muted leading-tight whitespace-nowrap">
                  Breeze.AI™ by{" "}
                  <span className="text-primary group-hover:underline">Accion Labs</span>
                </p>
              </div>
            )}
          </a>
        </div>
      </aside>

      {/* Spacer div to push content away from fixed sidebar */}
      <div className="w-20 flex-shrink-0" aria-hidden="true" />
    </>
  );
}
